<template>
  <div>
    <h2 class="mb-3">{{ $t('views.locations.purchaseeligibility.title') }}</h2>

    <b-row class="mb-3">
      <b-col md="8" class="mb-2 mb-md-0">
        <b-input-group>
          <b-form-input
            v-model.trim="searchQuery"
            :placeholder="$t('views.locations.purchaseeligibility.search.placeholder')"
          />
        </b-input-group>
      </b-col>
      <b-col md="4">
        <b-input-group>
          <b-input-group-prepend is-text>{{ $t('views.locations.purchaseeligibility.paging.pagesize') }}</b-input-group-prepend>
          <b-form-select v-model="pageSize" :options="pageSizeOptions" :disabled="isLoading" @change="onPageSizeChange" />
        </b-input-group>
      </b-col>
    </b-row>

    <b-alert v-if="errorMessage" variant="danger" show class="mb-3">{{ errorMessage }}</b-alert>

    <b-row class="mb-2" align-v="center">
      <b-col class="text-right">
        <small class="text-muted mr-2">{{ $t('views.locations.purchaseeligibility.paging.totalLabel') }}</small>
        <b-badge variant="light">{{ totalRows }}</b-badge>
      </b-col>
    </b-row>
    <b-row class="mb-2" align-v="center">
      <b-col class="text-right">
        <b-pagination
          v-model="page"
          :per-page="pageSize"
          :total-rows="totalRows"
          align="right"
          :disabled="isLoading"
          @change="onPageChange"
        />
      </b-col>
    </b-row>

    <b-table
      :items="rows"
      :fields="tableFields"
      :busy="isLoading"
      bordered
      striped
      hover
      responsive
      small
      show-empty
      :empty-text="$t('views.locations.purchaseeligibility.table.empty')"
    >
        <template #table-busy>
          <div class="text-center py-4">
            <b-spinner variant="primary" class="mr-2" />
            <span>{{ $t('views.locations.purchaseeligibility.table.loading') }}</span>
          </div>
        </template>

        <template #cell(product)="data">
          <div class="font-weight-bold">{{ data.item.name || '-' }}</div>
          <small class="text-muted">{{ data.item.productId }}</small>
        </template>

        <template #cell(internalArticleNumber)="data">
          <b-badge variant="light">{{ data.item.internalArticleNumber || '-' }}</b-badge>
        </template>

        <template #cell(eligibility)="data">
          <b-badge :variant="data.item.allowed ? 'success' : 'danger'">
            {{ data.item.allowed ? $t('views.locations.purchaseeligibility.eligibility.allowed') : $t('views.locations.purchaseeligibility.eligibility.denied') }}
          </b-badge>
        </template>

        <template #cell(reason)="data">
          <code>{{ data.item.reason || '-' }}</code>
          <small class="text-muted d-block mt-1 decision-explanation">{{ data.item.decisionExplanation || '-' }}</small>
        </template>

      <template #cell(matchedRule)="data">
        <div v-if="data.item.matchedRuleId">
            <small class="text-muted d-block">
              {{ $t('views.locations.purchaseeligibility.decomposition.canton') }}: {{ data.item.ruleParts.canton || '*' }}
            </small>
            <small class="text-muted d-block">
              {{ $t('views.locations.purchaseeligibility.decomposition.industryCode') }}: {{ data.item.ruleParts.industryCode || '*' }}
            </small>
            <small class="text-muted d-block">
              {{ $t('views.locations.purchaseeligibility.decomposition.customerNumber') }}: {{ data.item.ruleParts.customerNumber || '*' }}
            </small>
            <small class="text-muted d-block">
              {{ $t('views.locations.purchaseeligibility.decomposition.productGroup') }}: {{ data.item.ruleParts.productGroup || '*' }}
            </small>
            <small class="text-muted d-block">
              {{ $t('views.locations.purchaseeligibility.decomposition.productNumber') }}: {{ data.item.ruleParts.productNumber || '*' }}
            </small>
        </div>
        <span v-else>-</span>
      </template>

      <template #cell(matchedIndustryRight)="data">
        <div v-if="data.item.matchedIndustryRightId">
            <small class="text-muted d-block">
              {{ $t('views.locations.purchaseeligibility.decomposition.canton') }}: {{ data.item.industryRuleParts.canton || '*' }}
            </small>
            <small class="text-muted d-block">
              {{ $t('views.locations.purchaseeligibility.decomposition.industryCode') }}: {{ data.item.industryRuleParts.industryCode || '*' }}
            </small>
        </div>
        <span v-else>-</span>
      </template>
    </b-table>

    <b-row class="mt-3" align-v="center">
      <b-col class="text-right">
        <b-pagination
          v-model="page"
          :per-page="pageSize"
          :total-rows="totalRows"
          align="right"
          :disabled="isLoading"
          @change="onPageChange"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import MixinEntityBase from '@/components/mixins/EntityBase'

export default {
    name: 'Locations.PurchaseEligibility',
    mixins: [MixinEntityBase],
    data () {
        return {
            searchQuery: '',
            page: 1,
            pageSize: 20,
            totalRows: 0,
            isLoading: false,
            errorMessage: '',
            searchDebounceTimer: null,
            rows: [],
            pageSizeOptions: [10, 20, 50].map(v => ({ value: v, text: String(v) })),
            tableFields: [
                { key: 'product', label: this.$t('views.locations.purchaseeligibility.table.label.product') },
                { key: 'internalArticleNumber', label: this.$t('views.locations.purchaseeligibility.table.label.internalArticleNumber') },
                { key: 'eligibility', label: this.$t('views.locations.purchaseeligibility.table.label.eligibility'), class: 'text-center' },
                { key: 'reason', label: this.$t('views.locations.purchaseeligibility.table.label.reason') },
                { key: 'matchedRule', label: this.$t('views.locations.purchaseeligibility.table.label.matchedRule') },
                { key: 'matchedIndustryRight', label: this.$t('views.locations.purchaseeligibility.table.label.matchedIndustryRight') }
            ]
        }
    },
    methods: {
        onPageChange (nextPage) {
            this.page = nextPage
            this.loadProducts()
        },
        onPageSizeChange () {
            this.page = 1
            this.loadProducts()
        },
        buildSearchPayload () {
            const payload = {
                language: this.$store.getters['gui/getLanguage'],
                page: this.page,
                pageSize: this.pageSize,
                filters: [],
                sort: []
            }

            if (this.searchQuery) {
                payload.query = this.searchQuery
            }

            return payload
        },
        resolveProductExternalId (item = {}) {
            return item.id || null
        },
        decomposeRuleKey (key) {
            const parts = String(key || '').split(';')
            return {
                canton: parts[0] || null,
                industryCode: parts[1] || null,
                customerNumber: parts[2] || null,
                productGroup: parts[3] || null,
                productNumber: parts[4] || null
            }
        },
        decomposeIndustryRuleKey (key) {
            const parts = String(key || '').split(';')
            return {
                canton: parts[0] || null,
                industryCode: parts[1] || null
            }
        },
        async loadProducts () {
            this.isLoading = true
            this.errorMessage = ''

            try {
                const searchResponse = await this.$http.post('/search/api/Products/SearchByFilters', this.buildSearchPayload())
                const data = (searchResponse || {}).data || {}
                const products = data.result || []
                const responsePageNumber = data.pageNumber || this.page

                const identifiers = []
                const dedupe = new Set()
                products.forEach(item => {
                    const key = this.resolveProductExternalId(item)
                    if (key && !dedupe.has(key)) {
                        dedupe.add(key)
                        identifiers.push(key)
                    }
                })

                let decisionsByProduct = {}
                if (identifiers.length > 0) {
                    const eligibilityResponse = await this.$http.post('/catalog/api/PurchaseEligibility/products/check-batch', {
                        productExternalIds: identifiers,
                        context: {
                            locationId: this.eParent.id || null
                        },
                        source: 'admin.locations.purchaseeligibility'
                    })

                    const decisions = ((eligibilityResponse || {}).data || {}).result || []
                    decisionsByProduct = decisions.reduce((acc, item) => {
                        if (item && item.productExternalId) {
                            acc[item.productExternalId] = item.decision || {}
                        }
                        return acc
                    }, {})
                }

                this.rows = products.map(item => {
                    const productId = this.resolveProductExternalId(item)
                    const decision = decisionsByProduct[productId] || {}
                    const matchedRuleId = decision.matchedRuleId || null
                    const matchedIndustryRightId = decision.matchedIndustryRightId || null

                    return {
                        productId,
                        name: item.name,
                        internalArticleNumber: item.articleNumber || '-',
                        allowed: decision.allowed === true,
                        reason: decision.reason || null,
                        decisionExplanation: decision.decisionExplanation || null,
                        matchedRuleId,
                        matchedIndustryRightId,
                        decisionSource: decision.decisionSource || null,
                        ruleParts: this.decomposeRuleKey(matchedRuleId),
                        industryRuleParts: this.decomposeIndustryRuleKey(matchedIndustryRightId)
                    }
                })

                this.page = responsePageNumber
                this.totalRows = data.totalItems || products.length
            } catch (error) {
                this.errorMessage = this.$t('views.locations.purchaseeligibility.errors.loadingFailed')
                this.rows = []
                this.totalRows = 0
                console.error('Failed loading purchase eligibility page', error)
            } finally {
                this.isLoading = false
            }
        }
    },
    watch: {
        searchQuery () {
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer)
            }
            this.searchDebounceTimer = setTimeout(() => {
                this.page = 1
                this.loadProducts()
            }, 500)
        }
    },
    created () {
        this.loadProducts()
    },
    beforeDestroy () {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer)
            this.searchDebounceTimer = null
        }
    }
}
</script>

<style lang="scss" scoped>
code {
  font-size: 0.85rem;
}

.decision-explanation {
  white-space: pre-line;
  line-height: 1.3;
}
</style>
